import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/utils/api'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(JSON.parse(localStorage.getItem('user')) || null)
  const token = ref(localStorage.getItem('token'))
  const loading = ref(false)

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin' || user.value?.role === 'super_admin')

  function setAuthData(authToken, userData) {
    token.value = authToken
    user.value = userData
    localStorage.setItem('token', authToken)
    localStorage.setItem('user', JSON.stringify(userData))
    api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`
  }

  function clearAuth() {
    token.value = null
    user.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    delete api.defaults.headers.common['Authorization']
  }

  async function initializeAuth() {
    if (token.value) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
      try {
        const response = await api.get('/me')
        user.value = response.data.data
        localStorage.setItem('user', JSON.stringify(user.value))
      } catch {
        clearAuth()
      }
    }
  }

  async function login(credentials) {
    loading.value = true
    try {
      const response = await api.post('/login', credentials)
      const { token: authToken, user: userData } = response.data.data
      setAuthData(authToken, userData)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Login failed' }
    } finally {
      loading.value = false
    }
  }

  async function register(userData) {
    loading.value = true
    try {
      const response = await api.post('/register', userData)
      const { token: authToken, user: newUser } = response.data.data
      setAuthData(authToken, newUser)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.response?.data?.errors || 'Registration failed' }
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await api.post('/logout')
    } finally {
      clearAuth()
    }
  }

  async function updateProfile(profileData) {
    try {
      const response = await api.put('/profile', profileData)
      user.value = response.data.data
      return { success: true }
    } catch (error) {
      return { success: false, error: error.response?.data?.message }
    }
  }

  return {
    user,
    token,
    loading,
    isAuthenticated,
    isAdmin,
    login,
    register,
    logout,
    updateProfile,
    initializeAuth,
    setAuthData,
    clearAuth,
  }
})

